import { createFormContext, zodResolver } from '@mantine/form';
import {
  createEmployeeSchema,
  type CreateEmployee,
  defaultEmployee,
} from '@/schemas/API/POST/employee/request';
import { isEmptyForm } from '@/utils/functions';
import SectionLoadingOverlay from '@/components/Section/SectionLoadingOverlay';
import { useEffect } from 'react';
import { Card, CheckIcon, Group, SimpleGrid, Text, rem } from '@mantine/core';
import { PersonalInfoCard } from './Cards/PersonalInfoCard';
import { AddressCard } from './Cards/AddressCard';
import { IoMdClose } from 'react-icons/io';
import { useNavigate } from 'react-router-dom';
import { ContactCard } from './Cards/ContactCard';
import { FamilyContactCard } from './Cards/FamilyContactCard';
import { SaveCard } from './Cards/SaveCard';
import { postFetcher } from '@/api/fetchers';
import { notifications } from '@mantine/notifications';
import { MealVoucherCard } from './Cards/MealVoucherCard';
import { IdentityCard } from './Cards/IdentityCard';
import { EducationCard } from './Cards/EducationCard';
import { CourseCard } from './Cards/CourseCard';
import { RoleSelectCard } from './Cards/RoleSelectCard';
import { LocationsCard } from './Cards/LocationsCard';
import { displayError } from '@/utils/error/errorHandler';
import { TagsCard } from './Cards/TagsCard';
import { IconX } from '@tabler/icons-react';
import { modals } from '@mantine/modals';

export const [EmployeeFormProvider, useEmployeeFormContext, useEmployeeForm] =
  createFormContext<CreateEmployee>();

export const NewEmployeePage: React.FC = () => {
  const navigate = useNavigate();

  const form = useEmployeeForm({
    validate: zodResolver(createEmployeeSchema),
    validateInputOnBlur: true,
  });

  useEffect(() => {
    form.setInitialValues(defaultEmployee);
    form.setValues(defaultEmployee);
  }, []);

  const handelCancel = () => {
    if (!form.isDirty()) {
      navigate('/employees');
      return;
    }

    modals.openConfirmModal({
      title: 'Odejít bez uložení?',
      centered: true,
      children: (
        <Text>
          Formulář obsahuje změny, které nebyly uloženy. Určitě chcete formulář
          uzavřít?
        </Text>
      ),
      labels: { confirm: 'Odejít', cancel: 'Zrušit' },
      confirmProps: { color: 'red' },
      onConfirm: () => navigate('/employees'),
    });
  };

  const handleSave = async (): Promise<void> => {
    const validationResult = form.validate();
    if (validationResult.hasErrors) {
      console.error('Form failed validation:', validationResult.errors);
      notifications.show({
        title: 'Formulář se nepodařilo uložit.',
        message: 'Formulář obsahuje chyby.',
        color: 'red',
        icon: <IconX style={{ width: rem(20), height: rem(20) }} />,
      });
      return;
    }

    try {
      const response = await postFetcher('/employees', form.values);
      notifications.show({
        message: 'Zaměstnanec úspěšně přidán.',
        color: 'green',
        icon: <CheckIcon style={{ width: rem(20), height: rem(20) }} />,
      });
      navigate(`/employees/${response.id}/personal`);
    } catch (err) {
      displayError(err);
    }
  };

  if (!isEmptyForm(form)) {
    return (
      <div className="p-8">
        <EmployeeFormProvider form={form}>
          <Card shadow="sm" padding="md" radius="md" withBorder>
            <Group justify="space-between" align="start">
              <Text fw={700} size="xl" mb="sm">
                Nový zaměstnanec
              </Text>
              <IoMdClose
                size="28px"
                className="cursor-pointer duration-150 hover:text-red-700"
                onClick={handelCancel}
              />
            </Group>
            <RoleSelectCard />
            <LocationsCard />
            <PersonalInfoCard />
            <AddressCard />
            <SimpleGrid cols={2}>
              <ContactCard />
              <FamilyContactCard />
            </SimpleGrid>
            <SimpleGrid cols={2}>
              <MealVoucherCard />
              <IdentityCard />
            </SimpleGrid>
            <TagsCard />
            <EducationCard />
            <CourseCard />
            <SaveCard handleCancel={handelCancel} handleSave={handleSave} />
          </Card>
        </EmployeeFormProvider>
      </div>
    );
  }
  return <SectionLoadingOverlay />;
};
